import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** @type {boolean} */
  #syncingFromVariant = false;

  /** @type {number} */
  #pendingSyncToken = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#pendingSyncToken += 1;
  }

  /**
   * Handles a variant update event.
   * On PDP / featured product, gallery slides are selected by fcd-variant-gallery-sync.js
   * instead of rewriting the gallery HTML. Cards, quick-add, and combined-listing
   * product switches still replace the gallery.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const loadsNewProduct = Boolean(event.detail?.data?.newProduct);
    const isCardContext = Boolean(this.closest('product-card') || this.closest('quick-add-dialog'));

    if (!loadsNewProduct && !isCardContext) {
      return;
    }

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.#syncingFromVariant = true;
    this.#pendingSyncToken += 1;
    this.replaceWith(newMediaGallery);
  };

  /**
   * When the user selects a gallery image/slide, wait for the slideshow animation
   * to finish and then select the matching color swatch if one exists.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = async (event) => {
    if (this.#syncingFromVariant) {
      this.#syncingFromVariant = false;
      return;
    }

    if (!event.detail?.userInitiated) return;

    const mediaId =
      event.detail.id ||
      event.detail.slide?.querySelector?.('[data-media-id]')?.getAttribute('data-media-id') ||
      event.detail.slide?.getAttribute?.('slide-id');

    if (!mediaId) return;

    const token = ++this.#pendingSyncToken;
    const slideshow = this.slideshow;

    await this.#waitForSlideshowTransition(slideshow);

    // Ignore if a newer slide selection superseded this one, or the gallery was replaced.
    if (token !== this.#pendingSyncToken || !this.isConnected) return;

    this.#selectVariantForMedia(mediaId);
  };

  /**
   * Waits until the slideshow finishes its scroll/transition animation.
   * @param {import('./slideshow').Slideshow | undefined} slideshow
   * @returns {Promise<void>}
   */
  #waitForSlideshowTransition(slideshow) {
    return new Promise((resolve) => {
      if (!slideshow) {
        resolve();
        return;
      }

      let settled = false;
      /** @type {ReturnType<typeof setTimeout> | undefined} */
      let startTimer;
      /** @type {ReturnType<typeof setTimeout> | undefined} */
      let safetyTimer;

      const finish = () => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        if (startTimer) clearTimeout(startTimer);
        if (safetyTimer) clearTimeout(safetyTimer);
        resolve();
      };

      const observer = new MutationObserver(() => {
        if (!slideshow.hasAttribute('transitioning')) finish();
      });

      observer.observe(slideshow, {
        attributes: true,
        attributeFilter: ['transitioning'],
      });

      // Give the scroller a moment to set `transitioning`. If it never starts, resolve.
      startTimer = setTimeout(() => {
        if (!slideshow.hasAttribute('transitioning')) finish();
      }, 80);

      // Safety net for long / interrupted transitions.
      safetyTimer = setTimeout(finish, 1500);
    });
  }

  /**
   * Selects the variant-picker option that matches the given media id.
   * @param {string} mediaId
   */
  #selectVariantForMedia(mediaId) {
    const scope = this.closest('.shopify-section, dialog, product-information') || document;
    const picker = /** @type {HTMLElement & { updateSelectedOption?: (target: Element) => void } | null} */ (
      scope.querySelector('variant-picker')
    );
    if (!picker) return;

    const matchingInput = /** @type {HTMLInputElement | null} */ (
      picker.querySelector(`input[type="radio"][data-option-media-id="${String(mediaId).replace(/"/g, '')}"]`)
    );

    if (!matchingInput || matchingInput.checked) return;

    // Tell fcd-variant-gallery-sync to update the picker without moving the gallery.
    picker.dataset.fcdSyncFromGallery = 'true';
    window.setTimeout(() => {
      delete picker.dataset.fcdSyncFromGallery;
    }, 5000);

    if (typeof picker.updateSelectedOption === 'function') {
      picker.updateSelectedOption(matchingInput);
    } else {
      matchingInput.checked = true;
    }

    matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
